import * as React from "react";
import { Theme } from "@mui/material/styles";
import {
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../interfaces";
import {
  textHeaderColor,
  primaryHeaderColor,
} from "../../../refdata/constants";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import { Box, Divider, Typography } from "@mui/material";
import SearchPills from "../../../containers/SearchPills";
import { fileNameFormat } from "../../../common/services/Utilities";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import MultiHeaderSummaryTable from "../../MultiHeaderTable/MultiHeaderSummaryTable";
import {
  summaryTableHeader,
  transformDataForDownload,
} from "./SummaryTableUtilities";
import {} from "../BranchOfficeDeposits/BranchOfficeDepositsUtilities";
import ShowHideButton from "../../ShowHideButton";

interface Props {
  appState: Query;
}

interface TransformedData {
  [key: string]: {
    count: number;
    sum_DEPSUMBR: number;
    sum_BKMO: number;
  };
}

export default function SummaryTablesByAsset({ appState }: Props) {
  const assetSize: KeyValue = React.useMemo(() => {
    return {
      0: "Less than $25 Million",
      1: "$25 Million to $50 Million",
      2: "$50 Million to $100 Million",
      3: "$100 Million to $300 Million",
      4: "$300 Million to $500 Million",
      5: "$500 Million to $1 Billion",
      6: "$1 Billion to $3 Billion",
      7: "$3 Billion to $10 Billion",
      8: "Greater than $10 Billion",
    };
  }, []);

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat("Summary_Tables_By_Asset_Size")}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      footnotes: [
        "Includes FDIC-insured deposits in domestic offices (50 states and DC)",
        appState.filters.lastYear === "1994"
          ? "*Includes 18 conservatorships"
          : null,
        "**Does not include U.S. Branches of Foreign Banks whose deposits are included in the amounts reported by affiliated branches",
      ].filter(Boolean) as string[],
    },
  ];

  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );
  const [nationalTotals, setNationalTotals] = React.useState<KeyValue>({});
  const [downloadData, setDownloadData] = React.useState<KeyValue[]>();
  const [assetsData, setAssetsData] = React.useState<KeyValue>({
    upTo25Million: [],
    upTo50Million: [],
    upTo100Million: [],
    upTo300Million: [],
    upTo500Million: [],
    upTo1Billion: [],
    upTo3Billion: [],
    upTo10Billion: [],
    over10Billion: [],
  });
  const headingTitle = (
    <Box
      sx={{
        marginTop: -1,
        display: "flex",
        alignItems: "end",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        {summaryTableHeader(appState.ui.reportType)}
      </Typography>
      <ApiDataDownload
        app="SOD"
        apiData={apiData}
        downloadData={downloadData}
      />
    </Box>
  );
  const [loading, setLoading] = React.useState(false);
  const [previousYear, setPreviousYear] = React.useState(
    appState.filters.lastYear,
  );
  const [initialLoad, setInitialLoad] = React.useState(true);

  const assetSizeHeader: TableHeader[] = [
    {
      title: `Asset Size as of 6/30/${appState.filters.lastYear}`,
      styles: {
        backgroundColor: primaryHeaderColor,
        color: textHeaderColor,
        textAlign: "left",
      },
      colSpan: 3,
      apiField: "",
    },
  ];

  React.useEffect(() => {
    if (
      (appState.filters.lastYear !== previousYear || initialLoad) &&
      appState.ui.reportType
    ) {
      setLoading(true);
      initialLoad && setInitialLoad(false);
      setPreviousYear(appState.filters.lastYear);
      let isMounted = true;
      const dataRange = [
        "[0 TO 25000]",
        "[25001 TO 50000]",
        "[50001 TO 100000]",
        "[100001 TO 300000]",
        "[300001 TO 500000]",
        "[500001 TO 1000000]",
        "[1000001 TO 3000000]",
        "[3000001 TO 10000000]",
        ">10000000",
        ">=0",
      ];
      (async () => {
        await Promise.all(
          dataRange.map((range: string) => {
            return fetchData(
              `${ApiBaseUrl()}/sod?agg_by=INSURED&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${
                appState.filters.lastYear
              } AND ASSET:${range}`,
            );
          }),
        )
          .then((response) => {
            if (isMounted) {
              const transformResponse = (data: KeyValue) => {
                let result: KeyValue = {
                  IB: {
                    count: 0,
                    sum_BKMO: 0,
                    sum_DEPSUMBR: 0,
                  },
                  CB: {
                    count: 0,
                    sum_BKMO: 0,
                    sum_DEPSUMBR: 0,
                  },
                  SA: {
                    count: 0,
                    sum_BKMO: 0,
                    sum_DEPSUMBR: 0,
                  },
                };
                for (const dataElem of data.data) {
                  const transformedData: TransformedData = {
                    [dataElem.INSURED]: {
                      count: dataElem.count,
                      sum_DEPSUMBR: dataElem.sum_DEPSUMBR,
                      sum_BKMO: dataElem.sum_BKMO,
                    },
                  };
                  result = { ...result, ...transformedData };
                }
                const all = {
                  count: 0,
                  sum_BKMO: 0,
                  sum_DEPSUMBR: 0,
                };
                return { ...result, all: { ...all, ...data.totals } };
              };
              //transforms the data for a Summary table to be downloaded as XLS - Formatted

              const tableData: KeyValue = {
                upTo25Million: transformResponse(response[0]),
                upTo50Million: transformResponse(response[1]),
                upTo100Million: transformResponse(response[2]),
                upTo300Million: transformResponse(response[3]),
                upTo500Million: transformResponse(response[4]),
                upTo1Billion: transformResponse(response[5]),
                upTo3Billion: transformResponse(response[6]),
                upTo10Billion: transformResponse(response[7]),
                over10Billion: transformResponse(response[8]),
              };
              const allTotals = transformResponse(response[9]);
              const lastYear = `${appState.filters.lastYear}`;
              const resultForDownload = transformDataForDownload(
                { ...tableData, nationalTotals: allTotals },
                `6/30/${lastYear}`,
                "Asset Size",
              );

              setDownloadData(resultForDownload);
              setAssetsData(tableData);
              setNationalTotals(allTotals);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [appState.filters, initialLoad, previousYear, appState.ui, assetSize]);

  const footerSx = {
    color: (theme: Theme) => theme.palette.primary.main,
    width: 900,
    minWidth: 900,
    fontSize: 16,
    fontWeight: "bold",
  };

  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      <MultiHeaderSummaryTable
        tableData={assetsData}
        totalsData={nationalTotals}
        firstColumnData={assetSize}
        tableType="assetSize"
        firstHeader={assetSizeHeader}
        showAsterisks
        loading={loading}
        lastYear={appState.filters.lastYear}
      />
      <Typography sx={footerSx}>
        Includes FDIC-insured deposits in domestic offices (50 states and DC),
        Puerto Rico, and U.S. Territories
      </Typography>
      {appState.filters.lastYear === "1994" && (
        <Typography sx={footerSx}>*Includes 18 conservatorships</Typography>
      )}
      <Typography sx={footerSx}>
        **Does not include U.S. Branches of Foreign Banks whose deposits are
        included in the amounts reported by affiliated branches
      </Typography>
    </>
  );
}
